<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalFormProperties',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    editable: {
      type: Boolean,
      required: false,
      default: true,
    },
    rentalId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = () => {
    return $fn.value.fetch('list_rental_property', {
      parameters: { rental_id: props.rentalId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: { initialLoadSpinner: true },
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.editable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editable');
    }
  );
  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );

  const inst_evh_2095089031469157_close_jqqvhhk36_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_gzc5ql21c_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalProperty',
      props: {
        rentalId: props.rentalId,
      },
    };
  };
  const inst_evh_6853569216922577_close_1mssnhwwt_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_6853569216922577_showModal_3gb1ahq20_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalProperty',
      props: {
        rentalId: props.rentalId,
        resourceId: $event.scope.item.id,
      },
    };
  };
  const act_6492569393195423_request_nsj2tvq6s_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_rental_property',
      data: { ...$event.scope.item, rental_id: props.rentalId },
    };
  };
  const act_6492569393195423_request_nsj2tvq6s_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_zszpfn5iv =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4382dxikm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_zszpfn5iv,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4382dxikm],
    event: 'reload',
  });
  const act_2095089031469157_reloadComponentData_hr6vtyw2h =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2095089031469157_close_jqqvhhk36 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadComponentData_hr6vtyw2h,
      event: 'close',
      when: inst_evh_2095089031469157_close_jqqvhhk36_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_2095089031469157_showModal_gzc5ql21c =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_gzc5ql21c_getActionArgs,
      events: [inst_evh_2095089031469157_close_jqqvhhk36],
    });
  const inst_evh_2095089031469157_clickPreventStop_4wldolp12 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_gzc5ql21c,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_4wldolp12],
      event: 'click.prevent.stop',
    });
  const act_6853569216922577_reloadComponentData_42qp3x05w =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6853569216922577_close_1mssnhwwt =
    new actionTypes.EventHandlerImpl({
      action: act_6853569216922577_reloadComponentData_42qp3x05w,
      event: 'close',
      when: inst_evh_6853569216922577_close_1mssnhwwt_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_6853569216922577_showModal_3gb1ahq20 =
    new actionTypes.ShowModalAction({
      actionArgs: act_6853569216922577_showModal_3gb1ahq20_getActionArgs,
      events: [inst_evh_6853569216922577_close_1mssnhwwt],
    });
  const inst_evh_6853569216922577_clickPreventStop_l9k1wyfel =
    new actionTypes.EventHandlerImpl({
      action: act_6853569216922577_showModal_3gb1ahq20,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_6853569216922577_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6853569216922577_clickPreventStop_l9k1wyfel],
      event: 'click.prevent.stop',
    });
  const act_6492569393195423_reloadComponentData_4rdfhlbtx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6492569393195423_success_gya0qyg2l =
    new actionTypes.EventHandlerImpl({
      action: act_6492569393195423_reloadComponentData_4rdfhlbtx,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_6492569393195423_request_nsj2tvq6s = new actionTypes.RequestAction({
    actionArgs: act_6492569393195423_request_nsj2tvq6s_getActionArgs,
    confirm: act_6492569393195423_request_nsj2tvq6s_getConfirm,
    events: [inst_evh_6492569393195423_success_gya0qyg2l],
  });
  const inst_evh_6492569393195423_clickPreventStop_mq5knve43 =
    new actionTypes.EventHandlerImpl({
      action: act_6492569393195423_request_nsj2tvq6s,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_6492569393195423_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6492569393195423_clickPreventStop_mq5knve43],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_4382dxikm,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_jqqvhhk36,
    inst_evh_2095089031469157_clickPreventStop_4wldolp12,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_6853569216922577_close_1mssnhwwt,
    inst_evh_6853569216922577_clickPreventStop_l9k1wyfel,
    inst_evh_6853569216922577_clickPreventStop,
    inst_evh_6492569393195423_success_gya0qyg2l,
    inst_evh_6492569393195423_clickPreventStop_mq5knve43,
    inst_evh_6492569393195423_clickPreventStop,
  };

  const evh_7315092382398562_reload_4382dxikm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4382dxikm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_jqqvhhk36 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_jqqvhhk36.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_4wldolp12 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_4wldolp12.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6853569216922577_close_1mssnhwwt = async (event, scope) => {
    return await actions.inst_evh_6853569216922577_close_1mssnhwwt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6853569216922577_clickPreventStop_l9k1wyfel = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6853569216922577_clickPreventStop_l9k1wyfel.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6853569216922577_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6853569216922577_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6492569393195423_success_gya0qyg2l = async (event, scope) => {
    return await actions.inst_evh_6492569393195423_success_gya0qyg2l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6492569393195423_clickPreventStop_mq5knve43 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6492569393195423_clickPreventStop_mq5knve43.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6492569393195423_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6492569393195423_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    loading-spinner
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('RentalFormProperties', 'Properties') }}
          </strong>
          <ItpButton
            :text="gettext('Add property...')"
            size="sm"
            variant="light"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup v-if="loaded">
        <BListGroupItem class="card-body text-center" v-if="!items.length">
          <span>
            {{ pgettext('RentalFormProperties', 'No properties.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex align-items-start"
          v-for="item in items"
        >
          <div class="flex-grow-1" style="min-width: 0">
            <div class="d-flex flex-row">
              <div class="px-2">
                {{ item.property_unit.full_name }}
              </div>
              <div :id="item.property_unit.unit_type" class="px-2">
                {{ item.property_unit.unit_type }}
              </div>
            </div>
            <UcValidityPeriod
              :from="item.valid_from"
              :to="item.valid_to"
              class="px-2 my-1"
              v-if="item.valid_from || item.valid_to"
            >
            </UcValidityPeriod>
            <div class="d-flex flex-row">
              <ItpBadge
                variant="secondary"
                class="mx-2 shadow-none"
                v-if="item.locked"
              >
                <ItpIcon fa="lock" class="px-2"> </ItpIcon>
                <span>
                  {{ gettext('Locked') }}
                </span>
              </ItpBadge>
              <ItpBadge
                :text="gettext('Inactive')"
                variant="warning"
                class="mx-2 shadow-none"
                v-if="item.inactive"
              >
              </ItpBadge>
            </div>
          </div>
          <div class="ms-2 d-flex flex-row">
            <ItpButton
              variant="default"
              size="sm"
              icon="fa-edit"
              v-if="editable"
              @click.prevent.stop="
                evh_6853569216922577_clickPreventStop($event, { item })
              "
            >
            </ItpButton>
            <ItpButton
              variant="default"
              size="sm"
              icon="fa-trash"
              v-if="editable && !item.locked"
              @click.prevent.stop="
                evh_6492569393195423_clickPreventStop($event, { item })
              "
            >
            </ItpButton>
          </div>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
